import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useBlockStatus } from "../../hooks/useBlockStatus";
import Toast from "../shared/Toast/Toast";
import ChatImagePreviewModal from "./ChatImagePreviewModal/ChatImagePreviewModal";
import ChatRoomMessageList from "./ChatRoomMessageList/ChatRoomMessageList";
import styles from "./ChatRoomPage.module.scss";
import ChatRoomPageHeader from "./ChatRoomPageHeader/ChatRoomPageHeader";
import MessageInput from "./MessageInput/MessageInput";

const SCROLL_LOAD_MORE_THRESHOLD = 80;
const SCROLL_AT_BOTTOM_THRESHOLD = 5;
const SALE_CONTENT_TYPE = "sale";

/**
 * 채팅방 페이지 (01_CHAT 0.0.7, 0.0.8)
 * Figma: 40004313:31147 (대화 상세), 40004313:33192 (이미지 상세보기)
 * 구성: ChatRoomPageHeader → 날짜 구분 + 메시지 목록 → MessageInput
 * 0.0.8: 이미지 메시지 클릭 시 이미지 상세 모달(이미지 다운로드), 파일 메시지(다운받기)
 *
 * 헤더·메시지 발신자 표시명은 하위에서 `room` + userDisplayUtils로 결정.
 */
function ChatRoomPage({
  className = "",
  room = null,
  suid,
  messages = [],
  hasMore = false,
  historyLoading = false,
  onBack,
  onMoreClick,
  isMoreOpen = false,
  onMoreClose,
  onReport,
  onBlock,
  onUnblock,
  onNotification,
  onLeave,
  onLoadMore,
  onSendMessage,
  onFailedMessageRetry,
  onFailedMessageRemove,
  onSaleCardClick,
  reportToastMessage,
  notificationToastMessage,
}) {
  const otherUserId =
    room?.members?.find((m) => m.userId && m.userId !== window.loggedInUser?.uuid)?.userId ?? "";
  const { blockedByMe, blockedByOther } = useBlockStatus(otherUserId);

  // suid가 있으면 첫 메시지 전송 시 매물 카드를 함께 보냄 (최초 1회)
  const suidSentRef = useRef(false);

  /** 이미지 미리보기: 같은 메시지의 urls 안에서 index 이동 */
  const [imagePreview, setImagePreview] = useState(null);
  const messageAreaRef = useRef(null);
  const messageListRef = useRef(null);
  const savedScrollHeightRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const userJustSentRef = useRef(false);
  // saleCardReady 후 setStackPrice re-render로 DOM이 커졌을 때 ResizeObserver가 강제 스크롤하도록 하는 플래그
  const needsScrollAfterResizeRef = useRef(false);
  // 최초 메시지 로드 시 강제 스크롤용 플래그 (room 진입 후 첫 메시지 도착까지 true)
  const needsInitialScrollRef = useRef(true);

  // 내가 보냈을 때는 항상 맨 아래로, 그 외에는 맨 아래에 있었을 때만 스크롤 (prepend 제외)
  useLayoutEffect(() => {
    const el = messageAreaRef.current;
    if (!el) return;

    // 메시지가 비워지면 (방 전환 등) 초기 스크롤 플래그 리셋
    if (messages.length === 0) {
      needsInitialScrollRef.current = true;
      return;
    }

    // 최초 메시지 로드 — savedScrollHeightRef/isAtBottomRef 상태 무시하고 강제 최하단 스크롤
    if (needsInitialScrollRef.current) {
      needsInitialScrollRef.current = false;
      savedScrollHeightRef.current = null;
      el.scrollTop = el.scrollHeight;
      isAtBottomRef.current = true;
      return;
    }

    if (savedScrollHeightRef.current != null) {
      return;
    }
    const shouldScroll = userJustSentRef.current || isAtBottomRef.current;
    if (userJustSentRef.current) userJustSentRef.current = false;
    if (!shouldScroll) {
      return;
    }
    el.scrollTop = el.scrollHeight;
    isAtBottomRef.current = true;
  }, [messages]);

  // 이미지·폰트 등으로 scrollHeight가 늦게 늘어날 때 — 맨 아래에 있던 경우만 따라감
  useEffect(() => {
    const listEl = messageListRef.current;
    if (!listEl || typeof ResizeObserver === "undefined") return;

    const pinToBottomIfNeeded = () => {
      const el = messageAreaRef.current;
      if (!el || savedScrollHeightRef.current != null) return;
      if (!isAtBottomRef.current && !needsScrollAfterResizeRef.current) return;
      el.scrollTop = el.scrollHeight;
      isAtBottomRef.current = true;
      needsScrollAfterResizeRef.current = false;
    };

    let rafId = null;
    const ro = new ResizeObserver(() => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        pinToBottomIfNeeded();
      });
    });
    ro.observe(listEl);
    ro.observe(messageAreaRef.current);
    return () => {
      ro.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // 매물 카드(SaleContentCard)가 비동기로 렌더링된 직후 스크롤 보정
  // useLayoutEffect(paint 전)에서 dispatch되므로 handler도 paint 전에 실행됨
  useEffect(() => {
    const handleSaleCardReady = () => {
      const el = messageAreaRef.current;
      if (!el || savedScrollHeightRef.current != null || !isAtBottomRef.current) return;
      needsScrollAfterResizeRef.current = true;
      el.scrollTop = el.scrollHeight;
      isAtBottomRef.current = true;
    };
    window.addEventListener("messenger:saleCardReady", handleSaleCardReady);
    return () => window.removeEventListener("messenger:saleCardReady", handleSaleCardReady);
  }, []);

  // prepend 후 스크롤 위치 유지 (DOM 변경 직후 동기 실행, 페인트 전)
  useLayoutEffect(() => {
    const el = messageAreaRef.current;
    if (!el || savedScrollHeightRef.current == null) return;
    const prevHeight = savedScrollHeightRef.current;
    savedScrollHeightRef.current = null;
    const delta = el.scrollHeight - prevHeight;
    if (delta > 0) el.scrollTop += delta;
  }, [messages]);

  const handleScroll = useCallback(() => {
    const el = messageAreaRef.current;
    if (!el) return;
    isAtBottomRef.current =
      el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_AT_BOTTOM_THRESHOLD;
    if (!hasMore || historyLoading || !onLoadMore) return;
    if (el.scrollTop <= SCROLL_LOAD_MORE_THRESHOLD) {
      savedScrollHeightRef.current = el.scrollHeight;
      onLoadMore();
    }
  }, [hasMore, historyLoading, onLoadMore]);

  const openImagePreview = useCallback((url, allUrls, allNames, originalUrls) => {
    const urls = Array.isArray(allUrls) && allUrls.length > 0 ? [...allUrls] : url ? [url] : [];
    if (!urls.length) return;
    const idx = urls.indexOf(url);
    const names = urls.map((_, i) =>
      Array.isArray(allNames) && allNames[i] != null && allNames[i] !== "" ? allNames[i] : null
    );
    // originalUrls: 다운로드 시 원본 해상도 URL (없으면 표시용 URL 폴백)
    const dlUrls = Array.isArray(originalUrls) && originalUrls.length === urls.length ? [...originalUrls] : urls;
    setImagePreview({ urls, index: idx >= 0 ? idx : 0, names, downloadUrls: dlUrls });
  }, []);

  const closeImagePreview = useCallback(() => setImagePreview(null), []);

  const stepImagePreview = useCallback((delta) => {
    setImagePreview((prev) => {
      if (!prev || prev.urls.length < 2) return prev;
      const next = prev.index + delta;
      if (next < 0 || next >= prev.urls.length) return prev;
      return { ...prev, index: next };
    });
  }, []);

  const handleSend = useCallback(
    (payload) => {
      userJustSentRef.current = true;
      if (suid && !suidSentRef.current) {
        suidSentRef.current = true;
        onSendMessage?.({
          ...payload,
          content: { contentId: suid, contentType: SALE_CONTENT_TYPE },
        });
        return;
      }
      onSendMessage?.(payload);
    },
    [onSendMessage, suid]
  );

  return (
    <div
      className={`${styles.root} ${className}`.trim()}
      data-name="Chat room"
      data-node-id="40004313:31147"
    >
      <ChatRoomPageHeader
        room={room}
        blockedByMe={blockedByMe}
        blockedByOther={blockedByOther}
        onBack={onBack}
        onMoreClick={onMoreClick}
        isMoreOpen={isMoreOpen}
        otherUserId={otherUserId}
        onMoreClose={onMoreClose}
        onReport={onReport}
        onBlock={onBlock}
        onUnblock={onUnblock}
        onNotification={onNotification}
        onLeave={onLeave}
      />
      <div className={styles.content}>
        <div ref={messageAreaRef} className={styles.messageArea} onScroll={handleScroll}>
          <ChatRoomMessageList
            ref={messageListRef}
            messages={messages}
            room={room}
            listClassName={styles.messageList}
            onOpenImagePreview={openImagePreview}
            onFailedMessageRetry={onFailedMessageRetry}
            onFailedMessageRemove={onFailedMessageRemove}
            onSaleCardClick={onSaleCardClick}
          />
        </div>
      </div>
      <div className={styles.inputWrap}>
        <MessageInput
          onSendClick={handleSend}
          blockedByMe={blockedByMe}
          blockedByOther={blockedByOther}
          onUnblock={onUnblock}
        />
      </div>
      <ChatImagePreviewModal
        preview={imagePreview}
        onClose={closeImagePreview}
        onStep={stepImagePreview}
      />
      <Toast message={reportToastMessage} data-node-id="40004313:38030" />
      <Toast message={notificationToastMessage} data-node-id="40004313:51416" />
    </div>
  );
}

export default ChatRoomPage;
